import $ from 'jquery'
import { Controller } from 'stimulus'
import { throttle } from 'throttle-debounce'
import { useDebounce } from 'stimulus-use'
import { highlightTextNodes } from '~/common/helpers/text_highlight'
import './menu_component.scss'

const EXCLUDED_ITEM_CLASSES = [
  'opacity-0',
  'pointer-events-none',
  'h-0',
  'overflow-hidden',
  '!m-0',
  '!border-t-0',
]

// Treat punctuation as word boundaries so e.g. "coach" matches "Simplero.coach".
const WORD_SEPARATORS = /[\s.,;:!?()[\]{}"'/\\-]+/

export default class extends Controller {
  static classes = ['active']

  static debounces = [
    {
      name: 'runPendingSearch',
      wait: 100,
    },
  ]

  static targets = [
    'item',
    'emptyState',
    'notice',
    'excludedItem',
    'searchBox',
    'searchBoxContainer',
    'searchEmptyPlaceholder',
    'searchClear',
    'heading',
    'excludedHeading',
    'divider',
    'excludedDivider',
    'overflowIndicator',
  ]

  static values = {
    autoActivateOnConnect: Boolean,
    hasSections: Boolean,
    searchUrl: String,
    withSearch: Boolean,
    spinnerItemTemplate: String,
    cols: { type: Number, default: 0 },
  }

  connect() {
    useDebounce(this)
    this._keyboardNavigating = false
    this._onMouseMove = (event) => {
      if (!this._keyboardNavigating) return
      this._keyboardNavigating = false
      // Mouseover precedes mousemove on entry, so activate the row it skipped.
      const item = event.target.closest('[data-tabbable-item]')
      if (this.tabbableItems.includes(item)) this.activateItem(item, false)
    }
    this.element.addEventListener('mousemove', this._onMouseMove)

    if (this.autoActivateOnConnectValue) {
      // Keep the initial selection when a new pane opens under a stationary pointer.
      this._keyboardNavigating = true
      this.autoActivateItem()
    }

    requestAnimationFrame(() => this._refreshOverflowIndicator())

    if (this.hasSectionsValue) {
      this.grid = Object.values(
        Object.groupBy(
          this.element.querySelectorAll('.menu-item-section'),
          (el) => el.getBoundingClientRect().left
        )
      )

      this.grid.forEach((col, x) => {
        col.forEach((item, y) => {
          item.setAttribute('data-menu-grid-x', x)
          item.setAttribute('data-menu-grid-y', y)
        })
      })
    }
    // Lazy dropdowns replay their first keyboard action only after Stimulus has
    // installed this menu's action bindings and completed connect().
    this.dispatch('connected')
  }

  disconnect() {
    this.dispatch('disconnected')
    this._pendingSearchInput = null
    this.element.removeEventListener('mousemove', this._onMouseMove)
    this._removeScrollListener()
    cancelAnimationFrame(this._overflowIndicatorRaf)
  }

  /* ====== Public API ====== */
  onArrowDown(e) {
    e.preventDefault()
    this._keyboardNavigating = true
    this.colsValue > 0 ? this.moveInColumns(0, 1) : this.activateNextItem()
  }

  onArrowUp(e) {
    e.preventDefault()
    this._keyboardNavigating = true
    this.colsValue > 0 ? this.moveInColumns(0, -1) : this.activatePreviousItem()
  }

  onArrowRight(e) {
    e.preventDefault()
    this._keyboardNavigating = true

    if (this.colsValue > 0) {
      this.moveInColumns(1, 0)
    } else if (this.hasSectionsValue) {
      this.activateAdjacent('right')
    } else if (
      this.element.closest('.t-pane') &&
      this.element.closest('.t-pane').classList.contains('t-pane--active')
    ) {
      // If the menu is nested inside a pane, we only want to handle the right arrow key if the pane is active
      this.selectActiveItem()
    }
  }

  onArrowLeft(e) {
    e.preventDefault()
    this._keyboardNavigating = true
    if (this.colsValue > 0) this.moveInColumns(-1, 0)
    else if (this.hasSectionsValue) this.activateAdjacent('left')
  }

  onEnterKey(e) {
    e.preventDefault()
    const previouslyFocused = document.activeElement
    this.selectActiveItem()
    previouslyFocused?.focus()
  }

  // If the active item contains a toggleable input (e.g. a switch), spacebar
  // toggles it. Otherwise we let the space character through (so it can be
  // typed into the search box).
  onSpacebar(e) {
    if (!this.lastActiveItem) return
    const toggle = this.lastActiveItem.querySelector(
      'input[type="checkbox"], input[type="radio"]'
    )
    if (!toggle) return
    e.preventDefault()
    // Clicking a label moves focus to the checkbox, which would break further
    // keyboard nav since arrow/enter actions are bound to the search box.
    const previouslyFocused = document.activeElement
    const target = this.lastActiveItem.querySelector('label') || toggle
    target.click()
    previouslyFocused?.focus?.()
  }

  onItemHover = (e) => {
    // Ignore spurious mouseover events caused by DOM changes under a stationary cursor during keyboard nav
    if (this._keyboardNavigating) return
    this.activateItem(e.currentTarget, false)
  }

  // You can set data-auto-activate-priority on an item to make it the default active item (e.g when the menu is opened).
  // We'll pick the item with the most priority (highest number) to be the default active item.
  // This works even as you dynamically update menu content through updateContent() or excludeItemsBySelector()
  autoActivateItem() {
    this.resetSearch()
    this._refreshOverflowIndicator()

    if (!this.lastActiveItem) {
      const items = this.tabbableItems.filter(
        (item) => item.dataset.autoActivatePriority !== undefined
      )
      if (!items.length) return
      const item = items.reduce((prev, curr) =>
        parseInt(prev.dataset.autoActivatePriority, 10) >=
        parseInt(curr.dataset.autoActivatePriority, 10)
          ? prev
          : curr
      )
      this.activateItem(item)
    }
  }

  activateGivenItem(event) {
    const item = event.detail.item
    if (this.tabbableItems.includes(item)) {
      this.activateItem(item)
    }
  }

  deactivateActiveItem() {
    if (!this.lastActiveItem) return
    this.#closeNestedDropdown(this.lastActiveItem)
    this.lastActiveItem.classList.remove(...this.activeClasses)
    this.lastActiveItem = null
  }

  // Expects a list of menu items in event.detail.content
  updateContent(event) {
    const items = this.itemTargets
      .concat(this.excludedItemTargets)
      .concat(this.emptyStateTargets)
      .concat(this.noticeTargets)
    items.forEach((item) => {
      if (item.dataset.permanentItem) return
      item.remove()
    })
    this.element.insertAdjacentHTML('beforeend', event.detail.content)
    if (this.hasOverflowIndicatorTarget)
      this.element.appendChild(this.overflowIndicatorTarget)
    this.lastActiveItem = null
    this.onMenuContentChanged()
    this.dispatch('contentUpdated')
  }

  // Expects a selector string that targets menu items to be excluded in event.detail.selector
  excludeItemsBySelector(event) {
    // This is useful when you don't want to throw away the item but want it to be hidden from the list. E.g when working with a multi-select where picking an item should remove it from the dropdown, but if they unpick it, it should be added back.
    const selector = event.detail.selector

    // Store last active item and its index so we can restore it (or the next best thing) later after exclusion
    const lastActiveItem = this.lastActiveItem
    const lastActiveItemIndex = this.lastActiveItemIndex()

    this.resetExcludedItems()

    // Set new excluded items
    if (selector) {
      this.element.querySelectorAll(selector).forEach((el) => {
        if (el.dataset.menuTarget != 'item') return
        this.excludeItem(el)
      })
    }
    this.activateItem(
      this.tabbableItems.find((item) => item == lastActiveItem) ||
        this.tabbableItems[lastActiveItemIndex] ||
        this.tabbableItems[lastActiveItemIndex - 1]
    )
    this.onMenuContentChanged()
  }
  /* ====== End API ====== */

  resetExcludedItems() {
    this.excludedItemTargets.forEach((el) => this.includeItem(el))
  }

  resetExcludedHeadings() {
    this.excludedHeadingTargets.forEach((el) => this.includeHeading(el))
  }

  resetExcludedDividers() {
    this.excludedDividerTargets.forEach((el) => this.includeDivider(el))
  }

  excludedItemTargetConnected(target) {
    target.closest('li').classList.add(...EXCLUDED_ITEM_CLASSES)
    this.updateMenuOpacityBasedOnExcludedItems()
  }
  excludedItemTargetDisconnected(target) {
    target.closest('li').classList.remove(...EXCLUDED_ITEM_CLASSES)
    this.updateMenuOpacityBasedOnExcludedItems()
  }

  excludedHeadingTargetConnected(target) {
    target.closest('li').classList.add(...EXCLUDED_ITEM_CLASSES)
  }

  excludedHeadingTargetDisconnected(target) {
    target.closest('li').classList.remove(...EXCLUDED_ITEM_CLASSES)
  }

  excludedDividerTargetConnected(target) {
    target.closest('li').classList.add(...EXCLUDED_ITEM_CLASSES)
  }

  excludedDividerTargetDisconnected(target) {
    target.closest('li').classList.remove(...EXCLUDED_ITEM_CLASSES)
  }

  updateMenuOpacityBasedOnExcludedItems = throttle(30, () => {
    // Hide the tippy using opacity when everything has been excluded.
    // We don't want to mess with the dropdown internal state.
    const dropdownContent = this.element.closest(
      '[data-dropdown-target=content]'
    )
    if (!dropdownContent) return
    dropdownContent.style.display =
      this.hasExcludedItemTarget &&
      !this.hasItemTarget &&
      !this.hasSearchEmptyPlaceholderTarget
        ? 'none'
        : ''
  })

  get tabbableItems() {
    return this.itemTargets.filter((item) => item.dataset.tabbableItem)
  }

  get searchableItems() {
    return this.tabbableItems.concat(this.excludedItemTargets)
  }

  onMenuContentChanged(autoActivateItem = true) {
    if (autoActivateItem) this.autoActivateItem()
    $(this.element).changed() // Make tooltips work correctly
    this.dispatch('dropdown:reposition', { prefix: false })
    this._refreshOverflowIndicator()
  }

  selectActiveItem() {
    if (!this.lastActiveItem) return
    if (this.#openNestedDropdown(this.lastActiveItem)) return

    const itemToClick =
      this.lastActiveItem?.closest('.js-dropdown-select') &&
      this.lastActiveItem.closest('[data-value]')
        ? this.lastActiveItem
        : (this.lastActiveItem.matches('[data-trigger-modal]')
            ? this.lastActiveItem
            : this.lastActiveItem.querySelector(
                'a, button, input[type=file], [data-trigger-modal], span[data-behavior="custom-view--action"], span[data-action], span[data-target]'
              )) ||
          this.lastActiveItem.querySelector('label') ||
          this.lastActiveItem

    itemToClick.click()
  }

  activateNextItem() {
    this.activateItem(
      (this.lastActiveItem &&
        this.tabbableItems[this.lastActiveItemIndex() + 1]) ||
        this.tabbableItems[0]
    )
  }

  activatePreviousItem() {
    this.activateItem(
      (this.lastActiveItem &&
        this.tabbableItems[this.lastActiveItemIndex() - 1]) ||
        this.tabbableItems[this.tabbableItems.length - 1]
    )
  }

  // Navigate the visual grid produced by the CSS `columns-N` layout. Items flow
  // column-major (down the first column, then the next), so we derive the grid from
  // each item's on-screen x-position rather than its index — index math would walk
  // DOM order, not the visible column, which is the classic "jumps by N" bug.
  moveInColumns(dx, dy) {
    const grid = this.columnGrid()
    if (!grid.length) return
    if (!this.lastActiveItem) {
      this.activateItem(grid[0][0])
      return
    }

    let col = 0
    let row = 0
    grid.forEach((column, x) => {
      const y = column.indexOf(this.lastActiveItem)
      if (y !== -1) {
        col = x
        row = y
      }
    })

    const newCol = Math.max(0, Math.min(col + dx, grid.length - 1))
    const newRow = Math.max(0, Math.min(row + dy, grid[newCol].length - 1))
    this.activateItem(grid[newCol][newRow])
  }

  // Group tabbable items into visual columns by their on-screen x-position
  // (integer-like keys sort ascending → left-to-right), ordered top-to-bottom
  // within each column by their DOM order.
  columnGrid() {
    return Object.values(
      Object.groupBy(this.tabbableItems, (el) =>
        Math.round(el.getBoundingClientRect().left)
      )
    )
  }

  lastActiveItemIndex() {
    return this.tabbableItems.indexOf(this.lastActiveItem)
  }

  activateItem(item, scroll = true) {
    if (item) {
      if (this.lastActiveItem != item) {
        this.deactivateActiveItem()
      }
      this.lastActiveItem = item
      this.lastActiveItem.classList.add(...this.activeClasses)
      if (scroll && item.scrollIntoViewIfNeeded) {
        setTimeout(() => item.scrollIntoViewIfNeeded(), 0)
      }
      this.dispatch('itemActivated', {
        detail: { item, value: item.dataset.value },
      })
    }
  }

  includeItem(item) {
    item.dataset.menuTarget = 'item'
  }

  excludeItem(item) {
    item.dataset.menuTarget = 'excludedItem'
  }

  includeHeading(heading) {
    heading.dataset.menuTarget = 'heading'
  }

  excludeHeading(heading) {
    heading.dataset.menuTarget = 'excludedHeading'
  }

  // We consider a heading "empty" if it is not followed by a data-menu-target="item"
  // before reaching another heading
  headingIsEmpty(heading) {
    let nextItem = heading.nextElementSibling
    while (nextItem) {
      if (nextItem.dataset.menuTarget === 'item') {
        // if it's an item, we know it's not empty
        return false
      } else if (nextItem.dataset.menuTarget === 'excludedItem') {
        // excluded item -- let's see what the next item is
        nextItem = nextItem.nextElementSibling
      } else {
        // it's a heading or excluded heading or something that's not an item -- the heading is empty
        return true
      }
    }
    // we reached the end -- heading is empty
    return true
  }

  handleSearch(e) {
    this._pendingSearchInput = e.target
    this.runPendingSearch()
  }

  runPendingSearch() {
    this._applyPendingSearch()
  }

  // Keyboard navigation must see the typed query even before the debounce ends.
  // Consume pending work so its timer cannot later reset the keyboard selection.
  flushLocalSearch() {
    if (!this.hasSearchUrlValue) this._applyPendingSearch()
  }

  _applyPendingSearch() {
    const input = this._pendingSearchInput
    this._pendingSearchInput = null
    if (!input) return

    if (!this.withFixedDimensions) this.setFixedHeightAndWidth()

    const inputValue = input.value
    if (this.hasSearchClearTarget) {
      this.searchClearTarget.classList.toggle('hidden', !inputValue)
    }
    if (this.hasSearchUrlValue) {
      this.handleRemoteSearch(inputValue)
    } else {
      this.handleLocalSearch(inputValue)
    }
  }

  clearSearchBox() {
    this.searchBoxTarget.value = ''
    this._lastRemoteSearchRequest = null
    if (this.hasSearchClearTarget)
      this.searchClearTarget.classList.add('hidden')
    this.handleSearch({ target: this.searchBoxTarget })
    this._keepSearchFocus()
  }

  async handleRemoteSearch(inputValue) {
    if (!inputValue) {
      this.resetSearch()
      return
    }

    this.hideOverflowIndicator()

    const searchItem = this.searchItem
    const storeItemsBeforeSearch = !this.itemsBeforeSearch // Store initial state only on the initial query.
    if (storeItemsBeforeSearch) this.itemsBeforeSearch = []

    /* Hide all non search items */
    this.itemTargets.forEach((item) => {
      if (item == searchItem) return
      if (storeItemsBeforeSearch) this.itemsBeforeSearch.push(item)
      item.remove()
    })

    searchItem.insertAdjacentHTML('afterend', this.spinnerItemTemplateValue)

    const url = new URL(this.searchUrlValue, window.location)
    url.searchParams.set('query', inputValue)
    const request = fetch(url)
    this._lastRemoteSearchRequest = request
    const response = await request
    const json = await response.json()
    if (this._lastRemoteSearchRequest != request) return

    const spinnerItem = this.element.querySelector('.js-menu-item-spinner')
    spinnerItem.remove()

    searchItem.insertAdjacentHTML('afterend', json.items_html)
    this.onMenuContentChanged(false)

    const firstItem = this.tabbableItems[0]
    if (firstItem) {
      this.activateItem(firstItem)
    }
  }

  get searchItem() {
    return this.searchBoxTarget.closest('.js-menu-item')
  }

  // Text used to match an item against the query. Falls back to textContent because searchable items
  // can be intentionally hidden: `search_only` rows are collapsed with `h-0`/`opacity-0` (e.g. the model
  // picker's "Other" group) yet must stay findable by search. `innerText` is rendering-aware and can
  // return '' for such a collapsed row depending on the browser engine — the likely reason the model
  // picker couldn't find "Other" models (DeepSeek) in the iOS/WebKit web view even though it worked in
  // Chromium. textContent is visibility- and engine-independent, so it always yields the row's text.
  searchText(item) {
    return item.innerText || item.textContent
  }

  // [data-menu-label] is the standard hook for an item's title (MenuItemComponent renders it on
  // the label span — see menu_item_component.html.erb); .t-btn-main-label is the fallback for
  // items rendered via a content block instead (e.g. object picker buttons).
  labelElement(item) {
    return (
      item.querySelector('[data-menu-label]') ||
      item.querySelector('.t-btn-main-label')
    )
  }

  // Text used to rank a title match above a description-only match (ENG-33842). Falls back to
  // the item's full searchText when it has no distinct label element, so every match on such an
  // item counts as a "label" match — nothing to rank it against, same as today's behavior.
  labelText(item) {
    const labelEl = this.labelElement(item)
    return labelEl
      ? labelEl.innerText || labelEl.textContent
      : this.searchText(item)
  }

  handleLocalSearch(inputValue) {
    if (!inputValue) {
      this.resetSearch()
      return
    }

    this.hideOverflowIndicator()

    const inputValues = inputValue
      .toLowerCase()
      .split(WORD_SEPARATORS)
      .filter((t) => t.length > 0)
    this.searchableItems.forEach((item) => {
      let searchableValues = this.searchText(item)

      if (item.dataset.searchValue)
        searchableValues += ` ${item.dataset.searchValue}`

      searchableValues = searchableValues.toLowerCase().split(WORD_SEPARATORS)

      // We have an array of typed words and an array of searchable words for every option
      // If the searchable words of an option include all the typed words, it's a match
      // But it shuould allow partial matches. For example, if one of the typed words is "Cop", it should match "Copenhagen"
      if (
        inputValues.every((inputValue) =>
          searchableValues.some((searchableValue) =>
            searchableValue.startsWith(inputValue)
          )
        )
      ) {
        this.includeItem(item)
      } else {
        this.excludeItem(item)
      }
    })

    this.rankMatchesByLabel(inputValues)

    this.hideEmptyHeadings()
    this.hideStragglerDividers()

    this.adjustSectionHeaders()

    // To find the best match we perform a similar search algorithm but only on those items not excluded by the previous step
    // and we only compare input value with the label of each item to find the first item that matches all the typed words
    const bestMatch = this.tabbableItems.find((item) =>
      inputValues.every((inputValue) =>
        this.searchText(item)
          .toLowerCase()
          .split(WORD_SEPARATORS)
          .some((searchableValue) => searchableValue.startsWith(inputValue))
      )
    )

    this.handleSearchResults(bestMatch)
    this.onMenuContentChanged(false)
    this.element.classList.toggle('menu-search-active', inputValue != '')
    this.applyHighlights(inputValues)
  }

  // Ranks items whose label/title matches the typed query above items that match only via
  // description or other searchable text (ENG-33842) — e.g. searching "opus" should surface
  // "Opus 5" before "Kimi K3", whose description merely mentions Opus. Reordering is scoped to
  // each visually contiguous run of currently-visible items: a run flushes at any *visible*
  // heading/divider, so grouped/sectioned flat menus keep their section boundaries. Excluded
  // (hidden) items in between don't break a run — they're collapsed to zero height and don't
  // visually separate anything. Sectioned menus (MenuSectionComponent, `sections?` in
  // menu_component.rb) nest their items inside a wrapper div rather than as direct children of
  // this.element, so they're naturally untouched here — nothing to iterate.
  rankMatchesByLabel(inputValues) {
    this.originalChildOrder ??= Array.from(this.element.children)

    let run = []
    const flushRun = () => {
      if (run.length > 1) this.reorderRun(run, inputValues)
      run = []
    }

    Array.from(this.element.children).forEach((el) => {
      const target = el.dataset.menuTarget
      if (target === 'item') run.push(el)
      else if (target === 'heading' || target === 'divider') flushRun()
    })
    flushRun()
  }

  reorderRun(run, inputValues) {
    const ordered = run
      .slice()
      .sort(
        (a, b) =>
          this.labelMatchScore(b, inputValues) -
          this.labelMatchScore(a, inputValues)
      )
    if (ordered.every((item, i) => item === run[i])) return

    // A fixed anchor outside the run (untouched by the moves below) to insert relative to —
    // run[0] itself can't be used since it may be one of the items being moved.
    const afterRun = run[run.length - 1].nextElementSibling
    ordered.forEach((item) => this.element.insertBefore(item, afterRun))
  }

  labelMatchScore(item, inputValues) {
    const words = this.labelText(item).toLowerCase().split(WORD_SEPARATORS)
    const isMatch = inputValues.every((inputValue) =>
      words.some((word) => word.startsWith(inputValue))
    )
    return isMatch ? 1 : 0
  }

  // Local search reorders items in place (see rankMatchesByLabel) — restore the pre-search DOM
  // order so clearing the query doesn't leave results shuffled from the last query typed.
  // isConnected guards against re-inserting an item that removed itself from the menu mid-search
  // (e.g. the model picker's "Other" toggle — see showOtherItems).
  restoreOriginalOrder() {
    if (!this.originalChildOrder) return
    this.originalChildOrder.forEach((el) => {
      if (el.isConnected) this.element.appendChild(el)
    })
    this.originalChildOrder = null
  }

  adjustSectionHeaders() {
    Array.from(
      this.element.querySelectorAll('.menu-item-section-title')
    ).forEach((header) => {
      if (
        header.nextElementSibling.querySelectorAll(
          '.js-menu-item[data-menu-target="item"]'
        ).length === 0
      ) {
        header.classList.add('hidden')
      } else {
        header.classList.remove('hidden')
      }
    })
  }

  // After we perform a search, if this dropdown has items that are headings,
  // try to find those headings that are "empty", meaning there are no items left under their group
  // and if so, hide them
  hideEmptyHeadings() {
    this.headingTargets
      .concat(this.excludedHeadingTargets)
      .forEach((heading) => {
        if (this.headingIsEmpty(heading)) {
          this.excludeHeading(heading)
        } else {
          this.includeHeading(heading)
        }
      })
  }

  hideStragglerDividers() {
    this.dividerTargets
      .concat(this.excludedDividerTargets)
      .forEach((divider) => {
        if (this.isDividerAlone(divider)) {
          this.excludeDivider(divider)
        } else {
          this.includeDivider(divider)
        }
      })
  }

  includeDivider(divider) {
    divider.dataset.menuTarget = 'divider'
  }

  excludeDivider(divider) {
    divider.dataset.menuTarget = 'excludedDivider'
  }

  // We consider a heading "empty" if it is not followed by a data-menu-target="item"
  // before reaching another heading
  isDividerAlone(divider) {
    let nextItem = divider.nextElementSibling
    while (nextItem) {
      if (
        ['divider', 'excludedDivider'].includes(nextItem.dataset.menuTarget)
      ) {
        return true
      } else if (nextItem.dataset.menuTarget === 'heading') {
        return false
      }
      nextItem = nextItem.nextElementSibling
    }

    return false
  }

  handleSearchResults(bestMatch = null) {
    // The bestMatch or the first result will be automatically activated and ready to be selected
    // If no results, ensure there's no active item and show the empty placeholder
    const firstMatch = bestMatch || this.tabbableItems[0]
    if (firstMatch) {
      this.toggleEmptyPlaceholder(false)
      this.activateItem(firstMatch)
    } else {
      this.deactivateActiveItem()
      this.toggleEmptyPlaceholder()
    }
  }

  setFixedHeightAndWidth() {
    // Fix the height and width so it doesn't jump/change when removing elements, e.g. when performing a search
    // Using jQuery to get actual height/width, without padding/margin/etc

    // Ensure we only do this once
    this.withFixedDimensions = true

    this.element.style.width = getComputedStyle(this.element).width

    // First check if we're within a pane. If not, check if we're inside a tippy box
    const container =
      this.element.closest('.t-pane__body') ||
      (this.element.closest('.tippy-box') && this.element.parentElement)

    if (container) {
      this.element.style.minHeight = `${$(container).height()}px`
    }
  }

  toggleEmptyPlaceholder(show = true) {
    if (!this.hasSearchEmptyPlaceholderTarget) return
    this.searchEmptyPlaceholderTarget.classList.toggle('hidden', !show)
  }

  onSearchKeypress(e) {
    const { key } = e.detail
    const current = this.searchBoxTarget.value

    // Backspace on empty query is a no-op — don't reveal the search box for nothing
    if (key === 'Backspace' && current.length === 0) return

    // Show the search box if it was hidden (virtual search mode)
    if (!this.withSearchValue && this.hasSearchBoxContainerTarget) {
      this.searchBoxContainerTarget.classList.remove('hidden')
    }

    this.searchBoxTarget.focus()
    this.searchBoxTarget.value =
      key === 'Backspace' ? current.slice(0, -1) : current + key
    this.handleSearch({ target: this.searchBoxTarget })
  }

  resetSearch() {
    // Restoring the unfiltered list moves the search field's parent. Browsers
    // drop focus when that happens, so remember it before the reorder.
    const searchHadFocus = this._searchFieldFocused()
    this._pendingSearchInput = null
    this._lastRemoteSearchRequest = null
    if (this.hasSearchBoxTarget) {
      this.searchBoxTarget.value = ''
      // Don't re-hide the search box: hiding it (display:none) drops focus from
      // the input, so subsequent keystrokes go nowhere and the user can't keep
      // searching. Once revealed via virtual-search, stay visible until the
      // dropdown closes (which destroys/re-renders the menu).
    }
    if (this.hasSearchClearTarget)
      this.searchClearTarget.classList.add('hidden')
    this.element.classList.remove('menu-search-active')
    this.clearHighlights()
    this.restoreOriginalOrder()
    this.resetExcludedItems()
    this.resetExcludedHeadings()
    this.resetExcludedDividers()
    this.adjustSectionHeaders()
    this.resetRemoteSearch()
    this.toggleEmptyPlaceholder(false)
    // Re-hide items that are only meant to be visible during search (e.g. sub-dropdown options)
    this.searchOnlyItems.forEach((item) => this.excludeItem(item))
    // If the active item was a search-only item that just got hidden, clear the active state
    if (
      this.lastActiveItem &&
      !this.tabbableItems.includes(this.lastActiveItem)
    ) {
      this.deactivateActiveItem()
      if (this.tabbableItems[0]) this.activateItem(this.tabbableItems[0], false)
    }
    this._refreshOverflowIndicator()
    if (searchHadFocus) this._keepSearchFocus()
  }

  _searchFieldFocused() {
    return (
      this.hasSearchBoxTarget &&
      globalThis.document?.activeElement === this.searchBoxTarget
    )
  }

  // The clear control blurs the field on mousedown, and Stimulus reconnects the
  // moved field in a microtask that can blur a focus() call made too early.
  _keepSearchFocus() {
    if (!this.hasSearchBoxTarget) return

    const input = this.searchBoxTarget
    const focus = () => {
      if (input.isConnected) input.focus({ preventScroll: true })
    }
    focus()
    queueMicrotask(focus)
  }

  get searchOnlyItems() {
    return this.itemTargets
      .concat(this.excludedItemTargets)
      .filter((item) => item.dataset.searchOnly)
  }

  // Reveal items that are collapsed behind an inline "show more" toggle. They're rendered as
  // search-only items (hidden until a search matches them) sitting directly after their toggle,
  // so we reveal just this toggle's group: walk forward over the consecutive search-only rows and
  // strip the flag — otherwise resetSearch() re-hides them once the search box is cleared. Only
  // the clicked toggle is removed, so other groups (a second `sub_dropdown:`) stay collapsed.
  // Used by the model picker's "Other" group, which can't use a nested flyout because flyouts
  // don't open inside the mobile bottom sheet. See dropdown#reveal_toggle.
  showOtherItems(event) {
    event?.preventDefault()
    event?.stopPropagation()
    const toggle = event?.currentTarget
    if (!toggle) return

    let item = toggle.nextElementSibling
    while (item?.dataset.searchOnly) {
      delete item.dataset.searchOnly
      this.includeItem(item)
      item = item.nextElementSibling
    }
    toggle.remove()
    this.onMenuContentChanged(false)
  }

  resetRemoteSearch() {
    if (this.hasSearchUrlValue && this.itemsBeforeSearch != null) {
      let parent = this.searchItem

      // Remove everything except the search item
      this.itemTargets.forEach((item) => item != parent && item.remove())

      // Add items before search back
      this.itemsBeforeSearch.forEach((item) => {
        parent.insertAdjacentElement('afterend', item)
        parent = item
      })

      this.itemsBeforeSearch = null
    }
  }

  onSearchEscape(e) {
    if (this.searchBoxTarget.value) {
      this.resetSearch()
    } else {
      this.dispatch('dropdown:close', {
        prefix: false,
        target: this.element,
      })
    }
  }

  // Activates item on the left/right of the current item. Only works when we have sections.
  // Section titles will be ignored and the previous item will be selected.
  // Only works for left/right now, but we can also use it for up/down if we want to.
  activateAdjacent(direction) {
    if (!this.lastActiveItem) return

    const x = parseInt(this.lastActiveItem.dataset.menuGridX)
    const y = parseInt(this.lastActiveItem.dataset.menuGridY)

    let nextCol

    if (direction == 'right') {
      nextCol = x + 1
      if (nextCol > this.grid.length - 1) nextCol = 0 // wrap around
    } else {
      nextCol = x - 1
      if (nextCol < 0) nextCol = this.grid.length - 1 // wrap around
    }

    let adjacentItem = this.grid[nextCol][y]

    if (!adjacentItem) {
      // No adjacent item. Activate last item on adjacent column.
      let lastItemPosition = this.grid[nextCol].length - 1
      this.activateItem(this.grid[nextCol][lastItemPosition])
    }

    if (adjacentItem.dataset.tabbableItem) {
      // Activate if tabbable.
      this.activateItem(adjacentItem)
    } else {
      // This is a section title. Activate previous item in column.
      this.activateItem(this.grid[nextCol][y - 1])
    }
  }

  #getNestedDropdown(item) {
    return item.querySelector('& > .js-menu-item-dropdown')
  }

  #openNestedDropdown(item) {
    const dropdown = this.#getNestedDropdown(item)
    if (!dropdown) return false
    const trigger = dropdown.querySelector('[data-dropdown-target="button"]')
    if (trigger) {
      let triggerFocusable = trigger.querySelector('button, a, [tabindex]')
      if (!triggerFocusable) {
        trigger.tabIndex = 0
        trigger.classList.add('outline-none')
        triggerFocusable = trigger
      }
      triggerFocusable.focus()
    }
    this.dispatch('dropdown:open', { target: dropdown, prefix: false })

    return true
  }

  #closeNestedDropdown(item) {
    const dropdown = this.#getNestedDropdown(item)
    if (!dropdown) return false
    this.dispatch('dropdown:close', { target: dropdown, prefix: false })
    return true
  }

  applyHighlights(terms) {
    if (!terms || terms.length === 0) {
      this.clearHighlights()
      return
    }
    this.searchableItems.forEach((item) => {
      if (item.dataset.menuTarget !== 'item') return
      const labelEl = this.labelElement(item)
      if (!labelEl) return
      // Store original innerHTML so embedded HTML is preserved on restore
      if (!labelEl.dataset.originalLabel)
        labelEl.dataset.originalLabel = labelEl.innerHTML
      // Restore to original before re-applying (handles re-search)
      labelEl.innerHTML = labelEl.dataset.originalLabel
      // Highlight only direct text nodes, leaving child elements (icons, forms, etc.) untouched
      highlightTextNodes(labelEl, terms)
    })
  }

  clearHighlights() {
    this.element.querySelectorAll('[data-original-label]').forEach((el) => {
      el.innerHTML = el.dataset.originalLabel
      delete el.dataset.originalLabel
    })
  }

  hideOverflowIndicator() {
    if (!this.hasOverflowIndicatorTarget) return
    this.overflowIndicatorTarget.classList.add('opacity-0')
  }

  _refreshOverflowIndicator() {
    if (!this.hasOverflowIndicatorTarget) return

    // Defer to next frame so Stimulus's MutationObserver-driven target lifecycle
    // (e.g. excludedItemTargetConnected adding h-0) runs first and our measurement
    // sees the post-search layout, not the pre-search one.
    cancelAnimationFrame(this._overflowIndicatorRaf)
    this._overflowIndicatorRaf = requestAnimationFrame(() =>
      this._measureOverflowIndicator()
    )
  }

  _measureOverflowIndicator() {
    if (!this.hasOverflowIndicatorTarget) return

    const sc = this._findScrollContainer()
    const overflows = sc && sc.scrollHeight > sc.clientHeight

    if (sc !== this._overflowScrollContainer) {
      this._removeScrollListener()
      if (sc) {
        this._overflowScrollContainer = sc
        this._overflowScrollListener = () => this._refreshOverflowIndicator()
        sc.addEventListener('scroll', this._overflowScrollListener, {
          passive: true,
        })
      }
    }

    const atTop = !sc || sc.scrollTop < 5
    // Only real options count — the permanent search box is itself an `item`
    // target, so `hasItemTarget` is true even when the menu is empty.
    const hasItems = this.itemTargets.some(
      (item) => !item.dataset.permanentItem
    )
    const show = overflows && atTop && hasItems
    this.overflowIndicatorTarget.classList.toggle('opacity-0', !show)
  }

  _removeScrollListener() {
    if (this._overflowScrollContainer) {
      this._overflowScrollContainer.removeEventListener(
        'scroll',
        this._overflowScrollListener
      )
      this._overflowScrollContainer = null
      this._overflowScrollListener = null
    }
  }

  _findScrollContainer() {
    let el = this.element
    while (el) {
      const overflow = getComputedStyle(el).overflowY
      if (overflow === 'auto' || overflow === 'scroll') return el
      el = el.parentElement
    }
    return null
  }
}
